import React, { useRef } from 'react';
import { Sphere } from 'types/global';
import SphereAvatar from '../../molecules/sphere-avatar/sphere-avatar';
import { OverlayPanel } from 'primereact/overlaypanel';
import './PerspectiveAssigneesDisplayAtom.scss';

interface PerspectiveAssigneesDisplayAtomProps {
  assignees: Sphere[];
  size?: 'xsmall' | 'small' | 'medium' | 'large' | 'xlarge';
  maxVisible?: number;
  className?: string;
  mode?: 'edit' | 'view' | 'collapse';
}

const PerspectiveAssigneesDisplayAtom: React.FC<PerspectiveAssigneesDisplayAtomProps> = ({ assignees, size = 'small', maxVisible = 3, className = '', mode = 'edit' }) => {
  // mode prop reserved for future use
  const overlayRef = useRef<OverlayPanel>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  if (!assignees || assignees.length === 0) {
    return null;
  }

  const visibleAssignees = assignees.slice(0, maxVisible);
  const overflowCount = assignees.length - maxVisible;

  const getImageId = (sphere: Sphere): string | undefined => {
    if (sphere?.properties?.imageId) {
      if (Array.isArray(sphere.properties.imageId)) {
        return sphere.properties.imageId[0];
      }
      return sphere.properties.imageId;
    }
    return undefined;
  };

  const handleMouseEnter = (e: React.MouseEvent) => {
    overlayRef.current?.show(e, containerRef.current);

    // Adjust position after PrimeReact calculates it
    setTimeout(() => {
      const overlay = overlayRef.current?.getElement();
      if (overlay && containerRef.current) {
        const containerRect = containerRef.current.getBoundingClientRect();
        const overlayRect = overlay.getBoundingClientRect();

        // Position from right edge instead of left, with 50px offset to the left
        const rightPosition = containerRect.right - overlayRect.width - 50;
        overlay.style.left = `${rightPosition}px`;
      }
    }, 0);
  };

  const handleMouseLeave = () => {
    overlayRef.current?.hide();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleMouseEnter(e as any);
    }
  };

  return (
    <>
      <div
        ref={containerRef}
        className={`perspective-assignees-display ${className}`}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onKeyDown={handleKeyDown}
        onBlur={handleMouseLeave}
        role="button"
        tabIndex={0}
        aria-label={`View ${assignees.length} assignee${assignees.length > 1 ? 's' : ''}`}
      >
        {visibleAssignees.map((assignee) => (
          <SphereAvatar
            key={assignee.id}
            glyph={assignee.glyph}
            glyphColor={assignee.glyphColor}
            sphereId={assignee.id}
            sphereName={assignee.properties?.name}
            imageId={getImageId(assignee)}
            size={size}
            className="stacked-avatar"
          />
        ))}
        {overflowCount > 0 && <div className="overflow-badge">+{overflowCount}</div>}
      </div>
      <OverlayPanel ref={overlayRef} className="assignees-overlay" dismissable={false}>
        <div className="assignees-list">
          <div className="assignees-header">Assignees</div>
          {assignees.map((assignee) => (
            <div key={assignee.id} className="assignee-item">
              <SphereAvatar
                glyph={assignee.glyph}
                glyphColor={assignee.glyphColor}
                sphereId={assignee.id}
                sphereName={assignee.properties?.name}
                imageId={getImageId(assignee)}
                size="small"
              />
              <span className="assignee-name">{assignee.properties?.name}</span>
            </div>
          ))}
        </div>
      </OverlayPanel>
    </>
  );
};

export default PerspectiveAssigneesDisplayAtom;
